import React, { ReactNode } from 'react';
import { isPast } from 'date-fns';
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import storage from 'utils/storage';
import { toast } from 'components/Toast';
import { CONF_API_URL } from 'utils/config';

interface IAppProvider {
  children: ReactNode;
}
//leave gql for now otherwise will error out
const GRAPHQL_URL = process.env.REACT_APP_GRAPHQL_URL || ''; // e.g. https://dev.api.hazelhealth.com.au/graphql

const AppProvider = ({ children }: IAppProvider) => {
  const httpLink = createHttpLink({
    uri: GRAPHQL_URL
  });

  const authLink = setContext(async (_, { headers }) => {
    let token = storage.getItem('token');
    const expiry = storage.getItem('expiry');
    const refresh = storage.getItem('token-refresh');

    if (expiry && isPast(new Date(Number(expiry))) && refresh) {
      try {
        const res = await fetch(`${CONF_API_URL}api/hazel/doctors/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refresh })
        });
        const data = await res.json();

        if (res.ok && data?.code === 200) {
          token = data.token;
          storage.setItem('token', data.token);
          storage.setItem('token-refresh', data.refresh_token);
          storage.setItem('expiry', String(data.token_expires));
        } else {
          throw new Error(data?.message || 'Refresh token failed');
        }
      } catch (err) {
        toast({ title: 'Session expired', message: 'Please log in again.', variant: 'error' });
        storage.removeItem('token');
        storage.removeItem('token-refresh');
        storage.removeItem('expiry');
        token = null;
      }
    }

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : ''
      }
    };
  });

  const errLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors?.length) {
      graphQLErrors.forEach(({ message }) => {
        toast({ title: 'GraphQL error', message, variant: 'error' });
      });
    }
    if (networkError) {
      // show a toast here?
      // console.log(`[Network error]: ${networkError}`);
    }
  });

  const client = new ApolloClient({
    link: authLink.concat(errLink).concat(httpLink),
    cache: new InMemoryCache({ addTypename: false }),
    defaultOptions: {
      watchQuery: { errorPolicy: 'all' },
      query: { fetchPolicy: 'network-only', errorPolicy: 'all' }
    }
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default AppProvider;
